// ============================================================================
// ACCESS PENDING VIEW
//
// Shown when:
//   1. role === 'pending'   — user signed up but not yet provisioned by an admin
//   2. tenantId === null    — user created but not assigned to a tenant (non-platform-admin)
//   3. isActive === false   — account deactivated
//
// For pending/unprovisioned users — two parallel provisioning paths:
//
//   A) Invite flow (priority):
//      - On mount: queries /invites where recipientEmailLower == user.email + status=pending
//      - If found: shows "You've been invited!" card with "Accept Invite" button
//      - Accept calls claimInviteCF(inviteId) → Cloud Function provisions atomically
//      - After success: onRefresh() re-reads profile → RouteGuard routes them out
//
//   B) Manual join-request flow (fallback / shown below invite card):
//      - User enters Clinic ID → createJoinRequest() → success card + Check Status
//      - Check Status calls onRefresh() → if provisioned, routed out
//
// Blocks all navigation and Firestore reads until provisioned.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Clock, Shield, LogOut, Mail, CheckCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { createJoinRequest } from '../core/store.provisioning.js';
import { getPendingInvitesForEmail } from '../core/store.invites.js';
import { claimInviteCF } from '../core/functions.js';
import { ROLE_DISPLAY } from '../core/index.js';

// ---- helpers ----------------------------------------------------------------

function formatExpiry(ts) {
  if (!ts) return 'unknown date';
  const d = ts.toDate ? ts.toDate() : new Date((ts.seconds ?? 0) * 1000);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ---- component --------------------------------------------------------------

export default function AccessPendingView({ user, onLogout, onRefresh, errorMode = false }) {
  const isDeactivated = user && user.isActive === false;

  // Join-request flow state (manual path)
  const [tenantId, setTenantId]       = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [checking, setChecking]       = useState(false);

  // Invite-discovery flow state
  const [invites, setInvites]               = useState([]);
  const [inviteLoading, setInviteLoading]   = useState(false);
  const [inviteLoadError, setInviteLoadError] = useState('');
  const [claiming, setClaiming]             = useState(false);
  const [claimError, setClaimError]         = useState('');

  const showRequestForm = !errorMode && !isDeactivated;

  // Discover invites on mount (only for normal pending, not deactivated/errorMode)
  useEffect(() => {
    if (!showRequestForm || !user?.email) return;
    setInviteLoading(true);
    setInviteLoadError('');
    getPendingInvitesForEmail(user.email.toLowerCase())
      .then((found) => setInvites(found))
      .catch(() => setInviteLoadError('Could not check for invites. You can still request access manually.'))
      .finally(() => setInviteLoading(false));
  }, [showRequestForm, user?.email]);

  // Accept an invite via Cloud Function
  const handleAcceptInvite = async (inviteId) => {
    setClaiming(true);
    setClaimError('');
    try {
      await claimInviteCF(inviteId);
      // Reload profile — if provisioned, RouteGuard routes out automatically
      await onRefresh?.();
    } catch (err) {
      setClaimError(safeUserError(err) ?? 'Failed to accept invite. Please try again.');
      setClaiming(false);
    }
  };

  // Submit a manual join request
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!tenantId.trim()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await createJoinRequest(tenantId.trim(), user);
      setSubmitted(true);
    } catch (err) {
      if (err?.code === 'permission-denied') {
        setSubmitError(
          'A request for this clinic may already exist, or the Clinic ID is invalid. Check with your administrator.',
        );
      } else {
        setSubmitError(safeUserError(err) ?? 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Check status after manual join request
  const handleCheckStatus = async () => {
    if (!onRefresh) return;
    setChecking(true);
    try {
      await onRefresh();
    } finally {
      setChecking(false);
    }
  };

  const hasInvites = invites.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-800/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-slate-700/10 rounded-full blur-3xl" />

      <div className="relative z-10 text-center p-8 max-w-lg w-full mx-4">
        {/* Icon */}
        <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-8 ${
          errorMode ? 'bg-red-500/20' : isDeactivated ? 'bg-amber-500/20' : 'bg-slate-700/50'
        }`}>
          {errorMode ? (
            <Shield className="w-12 h-12 text-red-400" />
          ) : (
            <Clock className={`w-12 h-12 ${isDeactivated ? 'text-amber-400' : 'text-slate-400'}`} />
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl font-semibold text-white mb-3">
          {errorMode
            ? 'Access Not Provisioned'
            : isDeactivated
            ? 'Account Deactivated'
            : 'Access Pending'}
        </h1>

        {/* Message */}
        <p className="text-slate-400 text-base leading-relaxed mb-8">
          {errorMode
            ? 'Your user profile could not be read. You may not have been provisioned yet, or there was a permissions error. Contact your administrator.'
            : isDeactivated
            ? 'Your account has been deactivated. Contact your administrator to restore access.'
            : 'Your account has been created. Check below for an invite from your clinic, or enter your Clinic ID to request access manually.'}
        </p>

        {/* ── Invite discovery section ────────────────────────────────────── */}
        {showRequestForm && (
          <>
            {/* Loading invites */}
            {inviteLoading && (
              <div className="mb-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Checking for invites…
              </div>
            )}

            {/* Invite load error (non-blocking) */}
            {!inviteLoading && inviteLoadError && (
              <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-left">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-400">{inviteLoadError}</p>
              </div>
            )}

            {/* Invite cards */}
            {!inviteLoading && hasInvites && invites.map((inv) => (
              <div
                key={inv.id}
                className="mb-6 bg-violet-500/10 border border-violet-500/20 rounded-2xl p-5 text-left"
              >
                <p className="text-sm font-semibold text-violet-300 mb-1">You've been invited!</p>
                <p className="text-sm text-slate-300 mb-1">
                  Join <strong>{inv.tenantNameSnapshot || inv.tenantId}</strong> as{' '}
                  <strong>{ROLE_DISPLAY[inv.role]?.name ?? inv.role}</strong>
                </p>
                <p className="text-xs text-slate-500 mb-4">
                  Invited by your administrator · Expires {formatExpiry(inv.expiresAt)}
                </p>

                {claimError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-3">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-400">{claimError}</p>
                  </div>
                )}

                <button
                  onClick={() => handleAcceptInvite(inv.id)}
                  disabled={claiming}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all duration-200"
                >
                  {claiming && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {claiming ? 'Accepting…' : 'Accept Invite'}
                </button>
              </div>
            ))}
          </>
        )}

        {/* ── Manual join-request form ────────────────────────────────────── */}
        {showRequestForm && !submitted && (
          <div className="mb-8 text-left">
            {hasInvites && (
              <p className="text-xs text-slate-500 mb-3 text-center">
                Or request access manually with a Clinic ID
              </p>
            )}
            <form onSubmit={handleSubmitRequest} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Clinic ID
                </label>
                <input
                  type="text"
                  value={tenantId}
                  onChange={(e) => { setTenantId(e.target.value); setSubmitError(''); }}
                  placeholder="Provided by your administrator"
                  className="w-full px-4 py-3 rounded-xl bg-slate-800/70 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                  disabled={submitting}
                />
              </div>
              {submitError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-400">{submitError}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !tenantId.trim()}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all duration-200"
              >
                {submitting && <RefreshCw className="w-4 h-4 animate-spin" />}
                {submitting ? 'Requesting…' : 'Request Access'}
              </button>
            </form>
          </div>
        )}

        {/* Success Card — after manual join request submitted */}
        {showRequestForm && submitted && (
          <div className="mb-8 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm font-medium text-emerald-300">Request submitted successfully</p>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Your request has been sent to the clinic administrator. Once approved, click Check Status to continue.
            </p>
            <button
              onClick={handleCheckStatus}
              disabled={checking}
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 hover:text-white font-medium transition-all duration-200"
            >
              <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Checking…' : 'Check Status'}
            </button>
          </div>
        )}

        {/* What happens next (info box) */}
        {!errorMode && !isDeactivated && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 mb-8 text-left">
            <p className="text-sm font-medium text-slate-300 mb-2">What happens next?</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-start gap-2">
                <span className="text-slate-500 mt-0.5">1.</span>
                <span>If you were invited, accept the invite above — you'll be provisioned instantly.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 mt-0.5">2.</span>
                <span>Or enter your Clinic ID and click Request Access — a Tenant Admin will approve your request.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-500 mt-0.5">3.</span>
                <span>Click Check Status after approval — you'll be routed to your dashboard automatically.</span>
              </li>
            </ul>
          </div>
        )}

        {/* Account info */}
        {user && user.email && (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-8">
            <Mail className="w-4 h-4" />
            <span>Signed in as <span className="text-slate-400">{user.email}</span></span>
          </div>
        )}

        {/* Sign out */}
        <button
          onClick={onLogout}
          className="flex items-center justify-center gap-2 mx-auto px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-all duration-200 font-medium"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
